import { twMerge } from 'tailwind-merge';

import { DEFAULTS } from 'constants/defaults.constant';

// import Button from 'components/buttons/Button';
import Countdown from 'components/countdown/Countdown';
import { ReferralCountdownPart } from 'components/countdown/ReferralCountdownPart';

import { ScreenSize, useScreenSize } from 'hooks/useScreenSize';

import { parseHighlightedText } from '../../utils/parseHighlightedText';

import EventCompletedLogo from 'assets/single-event/event-completed.svg';
import singleEventBg from 'assets/single-event/referral_event_bg_1x.png';
import rmvChampionTrophyImage from 'assets/single-event/rmv-champion-trophy.png';

import { ISingleEventStage } from '../../types';
import { StageList } from '../StageList';
import { EventInfoPanel, EventInfoPanelProps } from './EventInfoPanel';
import styles from './IntroSection.module.scss';

type IntroSectionProps = {
  name: string;
  tagline: string;
  iconUrl: string | null;
  bannerUrl?: string | null;
  stages: ISingleEventStage[];
  endAt: number;
  variant?: 'singleEvent' | 'referral';
  children?: React.ReactNode;
  imageClassName?: string;
  customGradient?: string;
  eventInfo?: EventInfoPanelProps;
  withoutGradient?: boolean;
};

const IntroSection = ({
  name,
  tagline,
  iconUrl,
  bannerUrl,
  endAt,
  stages,
  variant = 'singleEvent',
  children,
  imageClassName,
  customGradient,
  eventInfo,
  withoutGradient = false,
}: IntroSectionProps) => {
  const variantStyles = {
    singleEvent: {
      backgroundImage: `url(${singleEventBg})`,
      title: '',
      valueClass: '',
      labelClass: 'tw-uppercase',
      labelsClass: 'tw-mt-[10px] tw-text-[16px]',
      valueWrapperClass: '',
      imageClass: imageClassName || 'tw-w-[303px]',
      contentClass: '',
    },
    referral: {
      backgroundImage: `url(${singleEventBg})`,
      title: 'tw-text-[32px] tw-leading-[42px] tw-mb-[8px]',
      valueClass: '!tw-text-[36px] tw-font-bold',
      labelClass: 'tw-capitalize',
      labelsClass: 'tw-mt-[8px] tw-text-[#20AA67]',
      valueWrapperClass: 'tw-w-[62px] tw-h-[62px]',
      imageClass: 'tw-w-[303px]',
      contentClass: 'lg:tw-pb-[100px]',
    },
  };
  const screenSize = useScreenSize();
  // Show max days only on mobile (extraSmall and small screens)
  const isMobile = screenSize === ScreenSize.extraSmall || screenSize === ScreenSize.small;

  // Use flexible width for days when showing total days on mobile
  const valueClass = isMobile
    ? 'tw-text-[42px] tw-font-bold tw-min-w-[60px] tw-h-[60px] tw-px-2'
    : 'tw-text-[42px] tw-font-bold tw-w-[60px] tw-h-[60px]';

  const variantStyle = variantStyles[variant];
  const isEventCompleted = endAt * DEFAULTS.tradeTimestampMultiplier < Date.now();

  return (
    <div className="tw-relative">
      <div
        className="tw-absolute tw-inset-0 tw-bg-cover tw-bg-center tw-bg-no-repeat"
        style={{ backgroundImage: `url(${bannerUrl || singleEventBg})` }}
      />
      {variant === 'referral' && !withoutGradient && (
        <div
          className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0"
          style={{
            height: '50%',
            background: `linear-gradient(to top, ${customGradient || 'rgba(0, 0, 0, 1)'} 0%, transparent 100%)`,
          }}
        />
      )}
      <div className={twMerge('tw-relative tw-pt-[60px]', variantStyle.contentClass)}>
        <div className="tw-mx-auto tw-max-w-[370px] lg:tw-max-w-[1280px]">
          <div className="tw-flex tw-items-start tw-justify-center tw-px-[50px] lg:tw-px-0">
            <p
              className={twMerge(
                'tw-max-w-[250px] tw-text-[24px] tw-font-bold tw-leading-[32px] tw-text-white lg:tw-max-w-none lg:tw-text-[32px] lg:tw-leading-[42px]',
                variantStyle.title
              )}
            >
              {name}
            </p>
            {eventInfo && <EventInfoPanel {...eventInfo} variant="mobile" />}
          </div>
          <div className="tw-flex tw-items-start tw-justify-center">
            <p className="tw-text-[42px] tw-font-bold tw-leading-[44px] tw-text-white lg:tw-text-[54px] lg:tw-leading-[64px]">
              {parseHighlightedText(tagline, variant)}
            </p>

            {eventInfo && <EventInfoPanel {...eventInfo} variant="desktop" />}
          </div>
        </div>
        <div className="tw-h-[30px] tw-w-full" />
        <div className="tw-relative tw-flex tw-items-center tw-justify-center">
          <img src={iconUrl || rmvChampionTrophyImage} alt={name} className={variantStyle.imageClass} />
          {isEventCompleted && (
            <img src={EventCompletedLogo} alt="Event completed" className="tw-absolute tw-h-[40px] tw-w-[270px]" />
          )}
        </div>
        <div className="tw-h-[30px] tw-w-full" />
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          {!isEventCompleted && (
            <Countdown
              label="Event ends in"
              endTimestamp={endAt * DEFAULTS.tradeTimestampMultiplier}
              labelClass={[
                'tw-text-white tw-text-[20px] tw-font-bold tw-leading-[24px] tw-mb-[16px]',
                variantStyle.labelClass,
              ]}
              valueClass={[valueClass, variantStyle.valueClass]}
              containerClass={[styles['intro-section-countdown-part-container']]}
              labelsClass={['tw-uppercase tw-font-medium tw-text-[15px] tw-leading-[24px]', variantStyle.labelsClass]}
              valueWrapperClass={[variantStyle.valueWrapperClass]}
              maxDaysOnly={isMobile}
              CountdownPartComponent={variant === 'referral' ? ReferralCountdownPart : undefined}
            />
          )}

          {children}
          <div className="tw-h-[32px] tw-w-full" />
          {/* NOTICE: temporary disabled */}
          {/* <Button text="Join now" size="medium" className="tw-my-[32px] tw-w-[138px]" onClick={onClickJoinNow} /> */}
          <div className="tw-w-full lg:tw-w-auto lg:tw-max-w-[1280px]">
            <StageList stages={stages} componentVariant={variant} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntroSection;
